import { useMutation } from '@tanstack/react-query';
import { type ItineraryDetail, type SaveItineraryPlanRequest } from '@utazzo/shared';
import { UzAlert, UzButton } from '@utazzo/ui';
import { ArrowLeft, Loader2 } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfigureStep } from '../components/ConfigureStep';
import * as itinerariesApi from '../itineraries.api';

import { useLead } from '@/features/leads/use-leads';

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return null;
};

/**
 * Draft itinerary create page (`/itineraries/new?leadId=…&from=lead|list`).
 *
 * Renders the existing ConfigureStep with a fake "draft" itinerary so the
 * operator can fill in cities, dates, room split, hotel source, visa, and
 * insurance BEFORE anything is written to the DB. Nothing persists until the
 * operator clicks **Generate day-by-day plan** - then we run a two-step save
 * in sequence:
 *
 *   1. `createItineraryFromLead`  (the resume-not-duplicate path keeps things
 *      safe on retries; tax rates are snapshotted from AppConfig here).
 *   2. `saveItineraryPlan`        (legs + rooms + flight cities -> day rows).
 *
 * On success we navigate to the proper editor URL and propagate `?from=` so
 * the editor's back link sends the operator back to the right place.
 *
 * If the user closes the tab BEFORE clicking Generate, no DB row exists and
 * the next "Create itinerary" click on the lead opens a fresh blank draft.
 */

export const NewItineraryPage = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const leadId = params.get('leadId') ?? null;
  const from = params.get('from') === 'list' ? 'list' : 'lead';

  const leadQuery = useLead(leadId);
  const lead = leadQuery.data ?? null;

  // One-shot mutation that creates the itinerary then saves the plan. We
  // wrap both in the same mutation so the ConfigureStep's busy state covers
  // the whole sequence and a single onSuccess fires the post-create
  // navigate. The createFromLead endpoint is resume-not-duplicate: retrying
  // after a transient failure picks up the existing draft row instead of
  // making a new one.
  const createAndSave = useMutation({
    mutationFn: async (payload: SaveItineraryPlanRequest) => {
      if (!leadId) throw new Error('Missing lead context');
      const created = await itinerariesApi.createItineraryFromLead(leadId, {});
      await itinerariesApi.saveItineraryPlan(created.id, payload);
      return created.id;
    },
    onSuccess: (newId) => {
      toast.success('Itinerary created');
      navigate(`/itineraries/${newId}?from=${from}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create the itinerary'),
  });

  // Build the fake "draft" itinerary handed to ConfigureStep. Empty string id
  // disables `useItineraryDays` (its `enabled` is `Boolean(itineraryId)`); the
  // useSaveItineraryPlan mutation hook is instantiated but never invoked
  // because we override `save` via the `saveOverride` prop.
  const draft = useMemo<ItineraryDetail | null>(() => {
    if (!lead) return null;
    // ConfigureStep only reads a small subset of ItineraryDetail (id, leadId,
    // customerName, pax counts, fromCityId, hotelSource, insuranceWanted,
    // legs, roomGroups, travelStartDate, visaId). We hand-build a defaults
    // object covering the schema's required fields and double-cast through
    // `unknown` to keep TS strict elsewhere - the empty-string id is what
    // disables `useItineraryDays` (enabled: Boolean(id)) and the explicit
    // `saveOverride` prop bypasses the never-fired internal save mutation.
    const blank: Partial<ItineraryDetail> = {
      id: '',
      legacyId: null,
      legacyAGId: null,
      kind: 'CUSTOM',
      status: 'IN_PROGRESS',
      title: '',
      summary: null,
      notesHtml: null,
      leadId: lead.id,
      leadReference: lead.reference ?? null,
      customerId: lead.customer.id,
      customerName: lead.customer.primaryName ?? lead.customer.displayName ?? null,
      assignedAgentId: null,
      assignedAgentName: null,
      fromPackageId: null,
      fromCityId: null,
      hotelSource: 'TBO',
      visaId: null,
      visaName: null,
      visaProcessingMode: null,
      insuranceWanted: false,
      insuranceId: null,
      insuranceName: null,
      travelStartDate: lead.expectedTravelStart ?? null,
      travelEndDate: lead.expectedTravelEnd ?? null,
      returnFlightDate: null,
      numberOfNights: 0,
      numberOfDays: 0,
      adultsCount: lead.travellerAdults ?? 2,
      childrenCount: lead.travellerChildren ?? 0,
      infantsCount: lead.travellerInfants ?? 0,
      roomsCount: 1,
      currencyId: null,
      currencyCode: 'INR',
      netCostPaise: '0',
      netMarkupPaise: '0',
      gstPaise: '0',
      tcsPaise: '0',
      gstRatePercent: null,
      tcsRatePercent: null,
      discountPaise: '0',
      grandTotalPaise: '0',
      markupByCategory: null,
      revisionNumber: 1,
      isLatestRevision: true,
      latestQuote: null,
      legs: [],
      roomGroups: [],
      inclusions: [],
      exclusions: [],
      termsHtml: null,
      cancellationPolicyHtml: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      deletedAt: null,
    };
    return blank as unknown as ItineraryDetail;
  }, [lead]);

  if (!leadId) {
    return (
      <div className="mx-auto max-w-2xl py-10">
        <UzAlert tone="warning">
          A lead is required to start a new itinerary. Open this page from a lead's "Create
          itinerary" button.
        </UzAlert>
      </div>
    );
  }
  if (leadQuery.isLoading || !draft || !lead) {
    return (
      <div className="flex h-[60vh] items-center justify-center gap-2 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading lead…
      </div>
    );
  }

  const backHref = from === 'list' ? '/itineraries' : `/leads/${lead.id}`;
  const backLabel = from === 'list' ? 'Back to itineraries' : 'Back to lead';

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={backHref}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label={backLabel}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-zinc-900">
              New itinerary for{' '}
              {lead.customer.primaryName ?? lead.customer.displayName ?? 'this lead'}
            </h1>
            <p className="mt-0.5 text-xs text-zinc-500">
              Fill in the trip details then click <strong>Generate day-by-day plan</strong>. Nothing
              is saved until you click generate.
            </p>
          </div>
        </div>
        <UzButton variant="ghost" size="sm" asChild>
          <Link to={backHref}>{backLabel}</Link>
        </UzButton>
      </header>

      <ConfigureStep
        itinerary={draft}
        onGenerated={() => {
          /* navigation is handled inside createAndSave.onSuccess */
        }}
        saveOverride={{
          mutate: (payload, opts) =>
            createAndSave.mutate(payload, {
              onSuccess: () => opts.onSuccess(),
              onError: (err) => opts.onError?.(err),
            }),
          isPending: createAndSave.isPending,
        }}
        saveCtaLabel="Generate day-by-day plan"
      />
    </div>
  );
};
